/**
 * PWAInstallModal.jsx — CORRIGÉ v2
 * ────────────────────────────────
 * Corrections :
 *  1. Bouton "Ouvrir Safari / Ouvrir Chrome" DIRECT après génération du lien
 *     (plus de deux étapes copier→puis cliquer).
 *  2. iOS : utilise window.open(link, '_blank') depuis un user-gesture pour
 *     forcer l'ouverture dans Safari (fonctionne dans la WebView Telegram).
 *  3. Android : intent URI robuste + fallback window.open.
 *  4. Le champ URL reste visible pour copie manuelle en secours.
 * Images : /emoji/safari2.png · chrome1.png · share1.png · logoapp1.png · tel1.png
 */

import React, { useState, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Copy, CheckCircle, ExternalLink } from 'lucide-react';
import { detectDevice } from './deviceDetect';
import { getApiAuthHeaders } from './utils/apiAuthFetch';
import { ensureDeviceIdSafe, resilientJsonRequest } from './utils/sessionRecovery';

// ── Helpers ───────────────────────────────────────────────────────────────────

const BASE_URL = (() => {
  try { return `${window.location.protocol}//${window.location.host}`; }
  catch { return ''; }
})();

// ── Chemins images ────────────────────────────────────────────────────────────
const IC = {
  safari  : '/emoji/safari2.png',
  chrome  : '/emoji/chrome1.png',
  share   : '/emoji/share1.png',
  logo    : '/emoji/logoapp1.png',
  download: '/emoji/tel1.png',
};

const ShareBadge = () => (
  <Ic src={IC.share} size={44} style={{ marginLeft: 6, marginRight: 4 }} />
);

const StepDenied = () => (
  <div style={{ textAlign: 'center', padding: '12px 4px 6px' }}>
    <div style={{ margin: '0 auto 18px', display: 'flex', justifyContent: 'center' }}>
      <Ic src={IC.logo} size={36} />
    </div>
    <h2 style={{ margin: '0 0 12px', fontSize: 20, fontWeight: 900 }}>Téléchargement indisponible</h2>
    <p style={{
      margin: 0,
      fontSize: 14,
      lineHeight: 1.65,
      color: 'rgba(191,219,254,0.86)',
      fontWeight: 500,
    }}>
      Seuls les clients ayant déjà finalisé au moins une commande peuvent télécharger l'application.
    </p>
  </div>
);

// ── Styles ────────────────────────────────────────────────────────────────────
const GLASS = {
  background: 'linear-gradient(135deg,rgba(74,144,226,0.18) 0%,rgba(30,60,140,0.28) 60%,rgba(10,20,80,0.42) 100%)',
  backdropFilter: 'blur(32px) saturate(220%) brightness(1.1)',
  WebkitBackdropFilter: 'blur(32px) saturate(220%) brightness(1.1)',
  border: '1.5px solid rgba(107,163,232,0.32)',
  boxShadow: '0 8px 40px rgba(0,0,0,0.45),inset 0 1px 0 rgba(255,255,255,0.15)',
};

const OVERLAY_S = {
  position: 'fixed', inset: 0, zIndex: 'var(--gs-z-lock, 4000)',
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  background: 'rgba(5,10,30,0.78)',
  backdropFilter: 'blur(6px)', WebkitBackdropFilter: 'blur(6px)',
  padding: '20px 16px',
};

const MODAL_S = {
  ...GLASS,
  borderRadius: 24, width: '100%', maxWidth: 400,
  padding: '28px 20px 22px',
  position: 'relative', overflow: 'hidden',
  color: '#fff', fontFamily: "'Inter',sans-serif",
  maxHeight: '90vh', overflowY: 'auto',
};

const BTN_BLUE = {
  width: '100%', padding: '13px 16px', borderRadius: 14,
  fontWeight: 800, fontSize: 14, color: '#fff', cursor: 'pointer',
  background: 'linear-gradient(135deg,rgba(96,165,250,0.70) 0%,rgba(37,99,235,0.65) 55%,rgba(29,78,216,0.70) 100%)',
  border: '1.5px solid rgba(191,219,254,0.55)',
  backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)',
  boxShadow: '0 8px 24px rgba(37,99,235,0.40),inset 0 1px 0 rgba(255,255,255,0.35)',
  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
  transition: 'opacity 0.18s',
};

const BTN_GLASS = {
  ...GLASS, borderRadius: 14, padding: '12px 16px',
  fontWeight: 700, fontSize: 13, color: 'rgba(255,255,255,0.88)',
  cursor: 'pointer', display: 'flex', alignItems: 'center',
  justifyContent: 'center', gap: 8, width: '100%',
};

const BTN_COPY = {
  background: 'rgba(255,255,255,0.08)',
  border: '1px solid rgba(255,255,255,0.2)',
  borderRadius: 8, padding: '5px 10px',
  color: 'rgba(191,219,254,0.85)', cursor: 'pointer',
  fontSize: 11, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 5,
  flexShrink: 0, whiteSpace: 'nowrap',
};

const SECTION_S = {
  ...GLASS, borderRadius: 16, padding: '15px 15px', marginBottom: 11,
};

const LABEL_S = { margin: '0 0 7px', fontSize: 13, fontWeight: 700, color: 'rgba(191,219,254,0.95)' };
const DESC_S  = { margin: 0, fontSize: 12, color: 'rgba(191,219,254,0.72)', lineHeight: 1.65 };

const STEP_NUM = {
  minWidth: 22, height: 22, borderRadius: '50%',
  background: 'rgba(74,144,226,0.35)', border: '1px solid rgba(107,163,232,0.4)',
  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
  fontSize: 11, fontWeight: 800, color: '#bfdbfe', flexShrink: 0,
};

const Ic = ({ src, size = 20, style = {} }) => (
  <img src={src} alt="" width={size} height={size}
    style={{ objectFit: 'contain', flexShrink: 0, display: 'inline-block', verticalAlign: 'middle', ...style }} />
);

// ── Ouvrir le lien dans le vrai navigateur externe ────────────────────────────
/**
 * iOS (WebView Telegram) :
 *   window.open() avec _blank est intercepté par Telegram et ouvre Safari ✔
 *   On utilise un <a> créé programmatiquement pour déclencher depuis un user-gesture.
 *
 * Android :
 *   Intent URI pour Chrome ; fallback window.open si l'intent échoue.
 */
const openLinkInBrowser = (link, isIos) => {
  if (!link) return;
  if (isIos) {
    // Crée un lien temporaire et clique dessus depuis le gestionnaire d'événement
    const a = document.createElement('a');
    a.href = link;
    a.target = '_blank';
    a.rel = 'noopener noreferrer';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  } else {
    // Android : intent URI pour forcer Chrome
    const intentUrl = `intent://${link.replace(/^https?:\/\//, '')}#Intent;scheme=https;package=com.android.chrome;end`;
    try {
      const a = document.createElement('a');
      a.href = intentUrl;
      a.target = '_blank';
      a.rel = 'noopener noreferrer';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    } catch {
      // Fallback universel
      window.open(link, '_blank', 'noopener,noreferrer');
    }
  }
};

// ── Étape 1 : Choix de la plateforme (auto-détecté en premier) ────────────────
const StepPlatform = ({ onSelect }) => {
  const detected = detectDevice();

  return (
    <div>
      <div style={{ textAlign: 'center', marginBottom: 22 }}>
        <div style={{ margin: '0 auto 14px', display: 'flex', justifyContent: 'center' }}>
          <Ic src={IC.logo} size={36} />
        </div>
        <h2 style={{ margin: 0, fontSize: 20, fontWeight: 900 }}>Installer l'appplication</h2>
        <p style={{ margin: '8px 0 0', fontSize: 13, color: 'rgba(191,219,254,0.80)', lineHeight: 1.55 }}>
          Global Strains est désormais disponible sur ton téléphone.
        </p>
      </div>

      {/* Auto-détection */}
      {detected.platform !== 'unknown' && (
        <div style={{
          marginBottom: 12, padding: '8px 12px', borderRadius: 10,
          background: 'rgba(34,197,94,0.12)', border: '1px solid rgba(34,197,94,0.28)',
          fontSize: 12, color: 'rgba(134,239,172,0.95)', textAlign: 'center',
        }}>
          compatible : <strong>{detected.model}</strong>
        </div>
      )}

      <p style={{ margin: '0 0 10px', fontSize: 12, color: 'rgba(191,219,254,0.55)', textAlign: 'center' }}>
       choisit ta platforme :
      </p>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        <motion.button
          style={{ ...BTN_BLUE, outline: detected.platform === 'ios' ? '2px solid rgba(191,219,254,0.7)' : 'none' }}
          whileTap={{ scale: 0.97 }}
          onClick={() => onSelect('ios')}
        >
          <Ic src={IC.safari} size={20} />
          iPhone (iOS)
        </motion.button>
        <motion.button
          style={{ ...BTN_GLASS, outline: detected.platform === 'android' ? '2px solid rgba(191,219,254,0.7)' : 'none' }}
          whileTap={{ scale: 0.97 }}
          onClick={() => onSelect('android')}
        >
          <Ic src={IC.chrome} size={20} />
          Android (Chrome)
        </motion.button>
      </div>
    </div>
  );
};

// ── Étape 2 : Tuto avec génération de lien ────────────────────────────────────
const StepTuto = ({ platform, chatId, onBack }) => {
  const [link, setLink]           = useState('');
  const [entryUrl, setEntryUrl]   = useState('');
  const [loading, setLoading]     = useState(false);
  const [copied, setCopied]       = useState(false);
  const [generated, setGenerated] = useState(false);
  const [openClicked, setOpenClicked] = useState(false);
  const [linkError, setLinkError] = useState('');
  const inputRef = useRef(null);
  const { model } = detectDevice();
  const isIos = platform === 'ios';
  const browserName = isIos ? 'Safari' : 'Chrome';
  const browserIcon = isIos ? IC.safari : IC.chrome;

  // ── Lier l'appareil au compte puis retourner l'URL racine propre ──
  //
  // Nouvelle logique sans token dans l'URL :
  //  1. Lire le deviceId (UUID) depuis localStorage — persisté depuis la 1ère visite
  //  2. Appeler /api/device/link pour enregistrer deviceId ↔ chatId côté serveur
  //  3. Le lien signet = https://domain.com/ (racine, aucun param, jamais tronqué)
  //
  // Quand le signet sera ouvert, App.js appellera /api/device/resolve avec ce même
  // deviceId et retrouvera le chatId → connexion transparente, modal nom jamais affiché.
  const generateLink = useCallback(async () => {
    if (!chatId || loading) return;
    setLoading(true);
    setLinkError('');
    try {
      // deviceId : généré à la volée s'il manque (plus de blocage silencieux)
      const deviceId = ensureDeviceIdSafe();
      if (!deviceId) {
        setLinkError("Impossible de préparer l'installation. Réessaie dans un instant.");
        return;
      }
      // Auth complète (initData/pwaToken/cookie) + réparation auto de session si 401 + 1 retry réseau
      const { response: res, data } = await resilientJsonRequest(() => fetch(`${BASE_URL}/api/device/link`, {
        method: 'POST',
        headers: getApiAuthHeaders({ 'Content-Type': 'application/json' }),
        credentials: 'include',
        body: JSON.stringify({ deviceId, chatId: String(chatId) }),
      }));
      if (data?.success) {
        // Signet = domaine seul. entryUrl pose le cookie puis redirige vers ce domaine.
        setLink(data.bookmarkUrl || `${BASE_URL}/`);
        setEntryUrl(data.entryUrl || `${BASE_URL}/`);
        setGenerated(true);
      } else {
        console.error('Erreur liaison device:', data?.error || res?.status);
        setLinkError(
          res?.status === 409
            ? 'Cet appareil est déjà lié à un autre compte.'
            : res?.status === 401
              ? 'Session expirée — rouvre l’app depuis Telegram puis réessaie.'
              : 'Téléchargement indisponible pour le moment. Réessaie.'
        );
      }
    } catch (err) {
      console.error('Erreur génération lien PWA:', err);
      setLinkError('Connexion instable. Réessaie.');
    } finally {
      setLoading(false);
    }
  // platform/model lus au moment du clic uniquement
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chatId, loading]);

  // ── Copier le lien dans le presse-papier ──
  const copyLink = useCallback(async () => {
    if (!link) return;
    try { await navigator.clipboard.writeText(link); }
    catch {
      if (inputRef.current) { inputRef.current.select(); document.execCommand('copy'); }
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 3000);
  }, [link]);

  // ── Ouvrir directement Safari ou Chrome (+ comptage 1× / client) ──
  const openInBrowser = useCallback(() => {
    const target = entryUrl || link;
    if (!target) return;
    openLinkInBrowser(target, isIos);
    setOpenClicked(true);
    // Comptabilise le clic install une seule fois côté serveur (flag après succès)
    if (chatId) {
      try {
        const already = sessionStorage.getItem(`gs_pwa_click_${chatId}`);
        if (!already) {
          const apiBase = (() => {
            try {
              const fromEnv = String(process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');
              if (fromEnv) return fromEnv.endsWith('/api') ? fromEnv : `${fromEnv}/api`;
            } catch { /* */ }
            return `${BASE_URL}/api`;
          })();
          fetch(`${apiBase}/client/pwa-event`, {
            method: 'POST',
            headers: {
              ...getApiAuthHeaders({ 'Content-Type': 'application/json' }),
              'x-chat-id': String(chatId),
            },
            body: JSON.stringify({ chatId: String(chatId), type: 'install_click' }),
            credentials: 'include',
          }).then((r) => {
            if (r.ok) {
              try { sessionStorage.setItem(`gs_pwa_click_${chatId}`, '1'); } catch { /* */ }
            }
          }).catch(() => {});
        }
      } catch { /* */ }
    }
  }, [entryUrl, link, isIos, chatId]);

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 18 }}>
        <button onClick={onBack} style={{
          background: 'rgba(255,255,255,0.10)', border: '1px solid rgba(255,255,255,0.2)',
          borderRadius: 10, color: '#fff', cursor: 'pointer', padding: '6px 12px',
          fontSize: 13, backdropFilter: 'blur(10px)', flexShrink: 0,
        }}>Retour</button>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, fontWeight: 700 }}>
          <Ic src={browserIcon} size={18} />
          {isIos ? 'iOS' : 'Android'} — {model}
        </div>
      </div>

      {/* ── Étape 1 : Générer & ouvrir le lien ── */}
      <div style={SECTION_S}>
        <p style={LABEL_S}>Étape 1 —  ouvrir le navigateur </p>
        <p style={{ ...DESC_S, marginBottom: 13 }}>
         ouvre ce lien dans ton navigateur afin de télécharger Global Strains sur ton appareil.
        </p>

        {!generated && linkError && (
          <p style={{ ...DESC_S, marginBottom: 10, color: 'rgba(252,165,165,0.95)', fontWeight: 700, textAlign: 'center' }}>
            {linkError}
          </p>
        )}

        {!generated ? (
          /* Bouton Générer */
          <motion.button
            style={{ ...BTN_BLUE, opacity: loading ? 0.7 : 1 }}
            whileTap={{ scale: loading ? 1 : 0.97 }}
            onClick={generateLink}
            disabled={loading}
          >
            <Ic src={IC.download} size={20} />
            {loading ? 'Téléchargement en cours…' : 'Télécharger'}
          </motion.button>
        ) : (
          <>
            {/* Champ URL + bouton copier inline */}
            <div style={{
              background: 'rgba(0,0,0,0.35)', borderRadius: 10, padding: '9px 12px',
              border: '1px solid rgba(107,163,232,0.3)', marginBottom: 10,
              display: 'flex', alignItems: 'center', gap: 8,
            }}>
              <input
                ref={inputRef}
                readOnly
                value={link}
                style={{
                  flex: 1, background: 'transparent', border: 'none',
                  color: 'rgba(191,219,254,0.75)', fontSize: 11,
                  fontFamily: 'monospace', outline: 'none', wordBreak: 'break-all',
                  minWidth: 0,
                }}
              />
              <button style={BTN_COPY} onClick={copyLink}>
                {copied
                  ? <><CheckCircle size={12} color="#4ade80" /> Copié</>
                  : <><Copy size={12} /> Copier</>
                }
              </button>
            </div>

            {/* ── BOUTON PRINCIPAL : Ouvrir Safari / Chrome directement ── */}
            <motion.button
              style={{ ...BTN_BLUE, gap: 10 }}
              whileTap={{ scale: 0.97 }}
              onClick={openInBrowser}
            >
              <Ic src={browserIcon} size={22} />
              <span>
                {openClicked
                  ? `${browserName} ouvert — continue dans le navigateur`
                  : `Ouvrir dans ${browserName}`
                }
              </span>
              <ExternalLink size={15} style={{ opacity: 0.7 }} />
            </motion.button>

            {/* Info si déjà cliqué */}
            {openClicked && (
              <p style={{ ...DESC_S, marginTop: 8, color: 'rgba(253,186,116,0.90)', textAlign: 'center' }}>
                {isIos
                  ? "Safari doit s'être ouvert. Passe à l'étape 2 ↓"
                  : "Chrome doit s'être ouvert. Passe à l'étape 2 ↓"
                }
              </p>
            )}
          </>
        )}
      </div>

      {/* ── Étape 2 : Navigateur (visible uniquement si lien généré) ── */}
      {generated && (
        <div style={SECTION_S}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 7 }}>
            <Ic src={browserIcon} size={20} />
            <p style={{ ...LABEL_S, margin: 0 }}>Étape 2 — Ajouter l'app à l'écran d'accueil</p>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {isIos ? (
              <>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <span style={STEP_NUM}>1</span>
                  <p style={{ ...DESC_S, margin: 0, display: 'flex', alignItems: 'center', flexWrap: 'wrap' }}>
                    Dans Safari, appuie sur l'icône <strong style={{ color: '#bfdbfe' }}>Partager</strong>
                    <ShareBadge /> en bas de l'écran
                  </p>
                </div>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                  <span style={STEP_NUM}>2</span>
                  <p style={{ ...DESC_S, margin: 0 }}>
                    défile et choisis <strong style={{ color: '#bfdbfe' }}>"Sur l'écran d'accueil"</strong>
                  </p>
                </div>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                  <span style={STEP_NUM}>3</span>
                  <p style={{ ...DESC_S, margin: 0 }}>
                   Confirme l'ajout de l'application.
                  </p>
                </div>
              </>
            ) : (
              <>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <span style={STEP_NUM}>1</span>
                  <p style={{ ...DESC_S, margin: 0, display: 'flex', alignItems: 'center', flexWrap: 'wrap' }}>
                  Dans Chrome, appuie sur l'icône <strong style={{ color: '#bfdbfe' }}>Partager</strong>
                  <ShareBadge /> en haut à droite de l'écran
                  </p>
                </div>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                  <span style={STEP_NUM}>2</span>
                  <p style={{ ...DESC_S, margin: 0 }}>
                    Sélectionne <strong style={{ color: '#bfdbfe' }}>"Ajouter à l'écran d'accueil"</strong>
                  </p>
                </div>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                  <span style={STEP_NUM}>3</span>
                  <p style={{ ...DESC_S, margin: 0 }}>
                    Confirme afin d'ajouter l'application à l'écran d'accueil.
                  </p>
                </div>
              </>
            )}
          </div>

          {isIos && (
            <p style={{ ...DESC_S, marginTop: 8, color: 'rgba(253,186,116,0.85)' }}>
              ⚠️ Ouvrez bien le navigateur afin de trouver le bouton "Ajouter à l'écran d'accueil".
            </p>
          )}

          <div style={{
            marginTop: 13, padding: '10px 12px', borderRadius: 10,
            background: 'rgba(34,197,94,0.12)', border: '1px solid rgba(34,197,94,0.28)',
            fontSize: 12, color: 'rgba(134,239,172,0.95)', lineHeight: 1.55,
            display: 'flex', alignItems: 'center', gap: 8,
          }}>
            <CheckCircle size={14} color="#4ade80" style={{ flexShrink: 0 }} />
            <span>
Plus besoin de passer par Telegram pour accéder à Global Strains.
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

// ── Composant principal ───────────────────────────────────────────────────────
const PWAInstallModal = ({ open, onClose, chatId, locked = false, denied = false }) => {
  const [step, setStep]         = useState('platform');
  const [platform, setPlatform] = useState(null);

  // Auto-détecter la plateforme et sauter l'étape de sélection si évident
  const detectedPlatform = React.useMemo(() => {
    const d = detectDevice();
    return d.platform !== 'unknown' ? d.platform : null;
  }, []);

  const handleSelectPlatform = useCallback((p) => { setPlatform(p); setStep('tuto'); }, []);
  const handleBack           = useCallback(() => { setStep('platform'); setPlatform(null); }, []);
  const handleClose          = useCallback(() => {
    onClose();
    setTimeout(() => { setStep('platform'); setPlatform(null); }, 350);
  }, [onClose]);

  // Auto-sélectionner la plateforme détectée dès l'ouverture
  React.useEffect(() => {
    if (open && detectedPlatform && step === 'platform') {
      setPlatform(detectedPlatform);
      setStep('tuto');
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  return createPortal(
    <AnimatePresence>
      {open && (
        <motion.div
          key="pwa-overlay"
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          transition={{ duration: 0.22 }}
          style={OVERLAY_S}
          onClick={(e) => { if (!locked && e.target === e.currentTarget) handleClose(); }}
        >
          <motion.div
            key="pwa-modal"
            initial={{ opacity: 0, scale: 0.92, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.92, y: 20 }}
            transition={{ duration: 0.28, ease: [0.25, 0.46, 0.45, 0.94] }}
            style={MODAL_S}
          >
            {/* Lumière ambiante */}
            <div style={{
              position: 'absolute', top: -80, left: -60, width: 260, height: 200,
              borderRadius: '50%',
              background: 'radial-gradient(circle,rgba(74,144,226,0.25) 0%,transparent 70%)',
              pointerEvents: 'none',
            }} />

            {!locked && (
              <button onClick={handleClose} style={{
                position: 'absolute', top: 12, right: 12,
                width: 32, height: 32, borderRadius: 10,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                background: 'rgba(255,255,255,0.10)',
                border: '1px solid rgba(255,255,255,0.22)',
                backdropFilter: 'blur(10px)', WebkitBackdropFilter: 'blur(10px)',
                color: 'rgba(255,255,255,0.8)', cursor: 'pointer', padding: 0,
              }}>
                <X size={16} />
              </button>
            )}

            <AnimatePresence mode="wait">
              <motion.div
                key={denied ? 'denied' : step}
                initial={{ opacity: 0, x: step === 'tuto' ? 30 : -30 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: step === 'tuto' ? -30 : 30 }}
                transition={{ duration: 0.2 }}
              >
                {denied && <StepDenied />}
                {!denied && step === 'platform' && <StepPlatform onSelect={handleSelectPlatform} />}
                {!denied && step === 'tuto'     && <StepTuto platform={platform} chatId={chatId} onBack={handleBack} />}
              </motion.div>
            </AnimatePresence>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body
  );
};

export default PWAInstallModal;